import { Box, CircularProgress, FormHelperText, Select as MuiSelect, SelectProps, SvgIcon } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import Subheader from './subheader';

type CommonProps = SelectProps & {
  label: string;
  hiddenLabel?: boolean;
  error?: boolean;
  dark?: boolean;
  stopPropagation?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  tooltipTitle?: string;
  tooltipLimit?: number;
  displayEmpty?: boolean;
  toggle?: { label: string; value: boolean; onChange: (value: boolean) => void };
  search?: { value: string; onChange: (event: any) => void };
  checkAll?: { value: boolean; onChange: () => void };
  action?: React.ReactNode;
  noResults?: {
    show?: boolean;
    text: React.ReactNode | string;
  };
  nested?: boolean;
  isLoading?: boolean;
};

interface SelectContentProps
  extends Omit<CommonProps, 'error' | 'helperText' | 'tooltipTitle' | 'tooltipLimit' | 'margin' | 'fullWidth'> {
  label: string;
  hiddenLabel?: boolean;
  dark?: boolean;
  value: unknown;
  onChange: SelectProps['onChange'];
  hasError: boolean;
  currentHelperText?: string;
  fieldOnChange?: (value: unknown) => void;
  handleTooltip: (bool: boolean) => void;
}

const SelectContent: React.FC<SelectContentProps> = ({
  label,
  hiddenLabel,
  value,
  onChange,
  toggle,
  search,
  action,
  checkAll,
  startAdornment,
  endAdornment,
  isLoading,
  children,
  noResults,
  nested,
  stopPropagation,
  hasError,
  currentHelperText,
  fieldOnChange,
  handleTooltip,
  dark = false,
  ...props
}) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  return (
    <Box flexDirection={'column'} display={'flex'} flex={1}>
      <MuiSelect
        {...props}
        onKeyDown={(e) => {
          stopPropagation && e.stopPropagation();
          props.onKeyDown && props.onKeyDown(e);
        }}
        className={backgroundClass}
        label={hiddenLabel ? '' : label}
        onMouseEnter={() => handleTooltip(true)}
        onMouseLeave={() => handleTooltip(false)}
        onClick={() => handleTooltip(false)}
        variant="outlined"
        autoWidth
        value={value}
        error={hasError}
        MenuProps={{
          autoFocus: false, // disable to set focus to search text field
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          sx: props.MenuProps
            ? props.MenuProps?.sx
            : {
                maxHeight: 400,
                zIndex: nested ? 1520 : 1500, // The component will override the theme's default props.
              },
          slotProps: {
            paper: {
              sx: {
                paddingX: 2,
                paddingY: 0,
              },
            },
          },
          elevation: 8,
          disableScrollLock: true,
        }}
        {...(startAdornment ? { startAdornment } : {})}
        {...(endAdornment ? { endAdornment } : {})}
        onChange={(event, child) => {
          if (fieldOnChange) {
            fieldOnChange(event.target.value);
          }
          if (onChange) {
            onChange(event, child);
          }
        }}
        IconComponent={(iconProps) =>
          isLoading ? (
            <CircularProgress {...iconProps} className={backgroundClass} size={18} />
          ) : (
            <SvgIcon {...iconProps} className={backgroundClass} fontSize="small">
              <ChevronDown />
            </SvgIcon>
          )
        }>
        {action || toggle || search || checkAll ? (
          <Subheader toggle={toggle} search={search} action={action} checkAll={checkAll} />
        ) : null}
        {noResults?.show ? (
          <Box
            sx={{
              pointerEvents: 'none',
            }}
            display={'flex'}
            justifyContent={'center'}
            padding={2}>
            {noResults.text}
          </Box>
        ) : (
          children
        )}
      </MuiSelect>
      {currentHelperText && (
        <FormHelperText error={hasError} disabled={props.disabled} className={backgroundClass}>
          {currentHelperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default React.memo(SelectContent);
